import { AuthCardLogInWithAppleButton } from "@/components/auth/apple/auth-card-log-in-with-apple-button";
import { observer } from "mobx-react-lite";
import { FC, FormEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "@/app/router/appRoutes";
import { appleIdentityModule } from "@/modules/apple-identity";
import { googleIdentityModule } from "@/modules/google-identity";
import { AuthCardLogInWithGoogleButton } from "@/components/auth/google/auth-card-log-in-with-google-button";
import { AuthCardBase } from "@/components/auth/auth-card-base";
import { usePublicAppStore } from "@/store";
import { LastUsedAuthMethod } from "@/domains/auth/last-used-auth-method";
import { AuthCardLastUsedBadge } from "@/components/auth/auth-card-last-used-badge";
import styled from "@emotion/styled";
import { readIMessageClaimFlowSession } from "@/modules/mem-agent/imessage-claim-flow-session";
import { MDSSeparator } from "@/mds/components/separator";
import { MDSButton, MDSButtonVariant, MDSTextField, MDSTextFieldVariant } from "@/mds";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";

export interface AuthCardLogInProps {
  prepareLogIn?: () => Promise<void> | void;
}

export const AuthCardLogIn: FC<AuthCardLogInProps> = observer(({ prepareLogIn }) => {
  const navigate = useNavigate();
  const { publicStore } = usePublicAppStore();
  const lastUsedAuthMethod = publicStore.auth.lastUsedAuthMethod;
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const logInWithGoogle = useCallback(async () => {
    await prepareLogIn?.();
    await googleIdentityModule.triggerLogIn();
  }, [prepareLogIn]);
  const logInWithApple = useCallback(async () => {
    await prepareLogIn?.();
    await appleIdentityModule.triggerLogIn({
      includeNativeChatOnboarding: !readIMessageClaimFlowSession(),
    });
  }, [prepareLogIn]);

  const continueWithEmailPassword = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const trimmedEmail = email.trim();

      if (!trimmedEmail) {
        trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
          step: "email",
          outcome: "validation_failed",
          failure_reason: "missing_input",
        });
        setErrorMessage("Enter your work email address");
        return;
      }

      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "email",
        outcome: "submitted",
      });
      setIsSubmitting(true);
      setErrorMessage(null);
      await prepareLogIn?.();
      trackEvent(TrackedEvent.EmailPasswordAuthFunnel, {
        step: "email",
        outcome: "succeeded",
        next_step: "password",
      });

      const searchParams = new URLSearchParams({
        email: trimmedEmail,
        mode: "continue",
      });
      navigate(`${appRoutes.logInEmailPassword({}).path}?${searchParams.toString()}`);
    },
    [email, navigate, prepareLogIn]
  );

  return (
    <AuthCardBase>
      <EmailForm onSubmit={continueWithEmailPassword}>
        <MDSTextField
          variant={MDSTextFieldVariant.Form}
          type="email"
          placeholder="Work email address"
          value={email}
          name="Work email address"
          onChange={setEmail}
          isDisabled={isSubmitting}
          aria-label="Work email address"
          autoFocus
        />
        {errorMessage && <FormError role="alert">{errorMessage}</FormError>}
        <ContinueButton variant={MDSButtonVariant.Contrast} isLoading={isSubmitting} type="submit">
          Continue
        </ContinueButton>
        {lastUsedAuthMethod === LastUsedAuthMethod.EmailPassword && <StyledLastUsedBadge />}
      </EmailForm>
      <StyledAuthMethodSeparator />
      <AuthMethodButtonWrapper>
        <AuthCardLogInWithGoogleButton onClick={logInWithGoogle} />
        {lastUsedAuthMethod === LastUsedAuthMethod.Google && <StyledLastUsedBadge />}
      </AuthMethodButtonWrapper>
      <AuthMethodButtonWrapper>
        <AuthCardLogInWithAppleButton onClick={logInWithApple} />
        {lastUsedAuthMethod === LastUsedAuthMethod.Apple && <StyledLastUsedBadge />}
      </AuthMethodButtonWrapper>
    </AuthCardBase>
  );
});

const AuthMethodButtonWrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const EmailForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  position: relative;
  width: min(320px, 100%);

  > div {
    width: 100%;
  }
`;

const ContinueButton = styled(MDSButton)`
  width: 100%;
`;

const FormError = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
`;

const StyledAuthMethodSeparator = styled(MDSSeparator)`
  margin: 0;
  width: 320px;
`;

const StyledLastUsedBadge = styled(AuthCardLastUsedBadge)`
  position: absolute;
  right: 16px;
  top: 0;
  transform: translateY(-50%);
`;
